/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import { Box, Divider, FormHelperText, Grid } from '@material-ui/core';
import { GridItem } from '../../../../pge-plus-common/Grid';
import { Question } from '../../../../pge-plus-common/Typography';
import useFormState from '../../../../../hooks/useFormState';
import { connectChargerToWifiValidateFunction } from '../rebateOnlyForm.rules';
import {
  getPreRebateEligibilityEvRebate,
  getPreRebateEligibilityConfirmationState,
  handleUnableToConnectAppError,
  navigateToIneligibleFiddlesticks,
} from '../rebateOnlyForm.utils';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import {
  ConnectChargerToWifiType,
  RebateQualification,
  UpdateSectionErrorCode,
} from '../rebateOnlyForm.types';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import RadioButtonGroup from '../../../../pge-plus-common/RadioButtonGroup';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import routes from '../../../../../routes';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  ApplicationErSectionType,
  ErRebateQualification,
  SectionErRebateConfirm,
} from '../../../../../__generated__/pgeplus-types';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import colors from '../../../../../themes/main-colors';

const ConnectChargerToWifi: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CHARGER_CONNECT_TO_WIFI,
  );
  const { content: contentMCG } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG,
  );
  const {
    formState: { rebateInfo, rebateConfirm, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();
  const chargerIsQualified = !!rebateInfo?.chargerValidation?.isQualifiedProduct;
  const vehicleIsQualified = !!rebateInfo?.vehicleValidation?.isQualifiedVehicle;

  const form = useFormState(
    rebateConfirm.rebateQualification!, //TODO: This should be taken from the global app state
    {
      validate: connectChargerToWifiValidateFunction(),
      validationContext: {
        content,
        params: { chargerIsQualified, vehicleIsQualified },
      },
    },
  );

  const questionInitial = contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_REBATE_ELIGIBILITY_CONNECT_CHARGER_QUESTION_WIFI',);
  let questionSecondary = content.get('ACTIVATED_CHARGER_YES_TEXT');

  const keySecondaryBoth = 'PGE_PLUS_EV_REBATES_FLOW_REBATE_ELIGIBILITY_CONNECT_CHARGER_QUESTION_SECONDARY_BOTH_QUALIFIED';
  const keySecondaryChargerOnly = 'PGE_PLUS_EV_REBATES_FLOW_REBATE_ELIGIBILITY_CONNECT_CHARGER_QUESTION_SECONDARY_QUALIFIED_CHARGER_ONLY';

  if (chargerIsQualified && vehicleIsQualified) {
    questionSecondary = contentMCG.get(keySecondaryBoth);
  } else if (chargerIsQualified) {
    questionSecondary = contentMCG.get(keySecondaryChargerOnly);
  }

  const { wrapWithLoader } = useWrapWithLoader();

  const sectionDataRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );
  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { setErrorNotification } = useUtil();

  const onSubmit = wrapWithLoader(async (data: RebateQualification) => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            rebateQualification: {
              ...data,
              doDriveTesla: !data?.wifiAvailable
                ? data?.doDriveTesla
                : undefined,
              haveChargerActivated: data?.wifiAvailable ? false : undefined,
            },
            connectTeslaToEVPulse: undefined,
            evPulse: data?.wifiAvailable ? undefined : rebateConfirm.evPulse,
            chargerInstallInfo: {
              ...rebateConfirm.chargerInstallInfo,
              installationCost:
                rebateConfirm.preRebateEligibility2?.evRebate?.__typename ===
                'RebateBYOC'
                  ? undefined
                  : rebateConfirm.chargerInstallInfo?.installationCost,
              chargerCost:
                rebateConfirm.preRebateEligibility2?.evRebate?.__typename ===
                'RebateBYOC'
                  ? undefined
                  : rebateConfirm.chargerInstallInfo?.chargerCost,
              receiptImages:
                rebateConfirm.preRebateEligibility2?.evRebate?.__typename ===
                'RebateBYOC'
                  ? undefined
                  : rebateConfirm.chargerInstallInfo?.receiptImages,
            },
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
      );

      // Save the response in a local var
      const result = response?.data?.updateSectionERRebateConfirm;

      // Check for errors to show the red banner and block navigation
      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      //Update the app state with the user selected values
      if (result?.section?.data) {
        const responseData = result?.section?.data;
        const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
          responseData,
        );

        updateFormState('rebateConfirm', updatedValues);
      }

      // Check for section errors to navigate to a specific fiddlesticks page based on the error code
      // CUP-24446: Refactor and include AC56 bugfix CUP-24522 - update fiddlesticks
      if (result?.section.errors.length) {
        const isUnableToConnectApp = result?.section.errors.some(
          error => error.code === UpdateSectionErrorCode.UnableToConnectApp,
        );
        if (
          isUnableToConnectApp &&
          chargerIsQualified &&
          vehicleIsQualified &&
          data?.wifiAvailable === false &&
          data?.willConnectVehicleManufacturerApp === false
        ) {
          navigateToIneligibleFiddlesticks();
          return;
        }
        if (handleUnableToConnectAppError(result?.section.errors)) {
          return;
        }
      }

      if (!response.data?.updateSectionERRebateConfirm.section.errors.length) {
        if (chargerIsQualified && vehicleIsQualified && data?.wifiAvailable === true ) {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_CHARGER_INSTRUCTIONS);
        } else if ( chargerIsQualified && vehicleIsQualified && data?.wifiAvailable === false) {
          if (data?.willConnectVehicleManufacturerApp === false) {
            navigateToIneligibleFiddlesticks();
          } else if (data?.willConnectVehicleManufacturerApp === true) {
            void navigate(routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN);
          }
        } else if (data?.wifiAvailable === false) {
          navigateToIneligibleFiddlesticks();
        } else if ( chargerIsQualified && !vehicleIsQualified && data?.wifiAvailable === true ) {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_CHARGER_INSTRUCTIONS);
        } else {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_CHARGER_INSTRUCTIONS);
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            rebateQualification: {
              ...form.values,
              doDriveTesla: !form.values?.wifiAvailable
                ? form.values?.doDriveTesla
                : undefined,
              haveChargerActivated: form.values?.wifiAvailable ? false : undefined,
            },
            connectTeslaToEVPulse: undefined,
            evPulse: form.values?.wifiAvailable
              ? undefined
              : rebateConfirm.evPulse,
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Fragment>
      <form
        onSubmit={form.submit(values => onSubmit(values), console.log)}
        noValidate
      >
        <Grid
          container
          css={{
            rowGap: 24,
          }}
        >
          {/* Initial Question  */}
          <GridItem>
            <Question
              title={questionInitial}
              popoverContent={content.get('WIFI_LEARN_MORE_TOOLTIP')}
              popoverLinkContent={content.get('REUSABLE_LEARN_MORE')}
            />
          </GridItem>
          <GridItem>
            <Box
              css={{
                width: 'max-content',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: true,
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: false,
                  },
                ]}
                state={form.values?.wifiAvailable}
                setState={e => form.setValue('wifiAvailable', e)}
              />
              {form.errors?.wifiAvailable && (
                <FormHelperText error>
                  {form.errors?.wifiAvailable}
                </FormHelperText>
              )}
            </Box>
          </GridItem>

          {chargerIsQualified && vehicleIsQualified &&
            form.values?.wifiAvailable !== undefined && 
            form.values?.wifiAvailable === false && (
              <Grid
                container
                css={{
                  rowGap: 24,
                }}
              >
                {/* Secondary Question */}
                <GridItem>
                  <Question
                    title={questionSecondary}
                    popoverContent={content.get('WIFI_LEARN_MORE_TOOLTIP')}
                    popoverLinkContent={content.get('REUSABLE_LEARN_MORE')}
                  />
                </GridItem>
                <GridItem>
                  <Box
                    css={{
                      width: 'max-content',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                  >
                    <RadioButtonGroup
                      options={[
                        {
                          label: content.get('REUSABLE_YES') || '',
                          value: true,
                        },
                        {
                          label: content.get('REUSABLE_NO') || '',
                          value: false,
                        },
                      ]}
                      state={form.values.willConnectVehicleManufacturerApp}
                      setState={e => form.setValue('willConnectVehicleManufacturerApp', e)}
                    />
                    {form.errors.willConnectVehicleManufacturerApp && (
                      <FormHelperText error>
                        {form.errors.willConnectVehicleManufacturerApp}
                      </FormHelperText>
                    )}
                  </Box>
                </GridItem>
              </Grid>
            )}
          <GridItem>
            <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />
            <ProceedOrCancelButton
              back={routes.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION}
              // CUP-24446: Refactor only, no behavior change
              backRouteState={{
                ...getPreRebateEligibilityConfirmationState(
                  getPreRebateEligibilityEvRebate(
                    rebateConfirm.preRebateEligibility2,
                    rebateConfirm.preRebateEligibility1,
                  ),
                  false,
                  false,
                  rebateConfirm.rebateInfo?.existingEnrollments?.iqbd,
                  rebateConfirm.iqbd?.checkEligibility,
                ),
                nextButtonRoute: routes.PGE_PLUS_REBATE_ONLY_CONNECT_CHARGER,
              }}
              disable={
                form.values?.wifiAvailable === undefined ||
                form.values?.wifiAvailable === null ||
                (chargerIsQualified &&
                  vehicleIsQualified &&
                  form.values?.wifiAvailable === false &&
                  (form.values?.willConnectVehicleManufacturerApp === undefined ||
                    form.values?.willConnectVehicleManufacturerApp === null))
              }
            />
          </GridItem>
        </Grid>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </form>
    </Fragment>
  );
};

export default ConnectChargerToWifi;
